import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Sparkles, Wrench } from 'lucide-react';
import { agruparPorData, dataReferenciaBr } from '../lib/agruparPorData';

export interface NovidadeTiModalItem {
    id: string;
    titulo: string;
    descricao?: string | null;
    categoria: 'novidade' | 'correcao';
    tag?: string | null;
    concluido_em: string;
    data_referencia: string;
    concluidoPorNome?: string | null;
}

interface NovidadesTiModalProps {
    itens: NovidadeTiModalItem[];
    onVisualizado: () => void;
    onConfirmar: () => Promise<void> | void;
}

const CATEGORIA_CFG: Record<NovidadeTiModalItem['categoria'], { label: string; color: string; Icon: typeof Sparkles }> = {
    novidade: { label: 'Novidade', color: 'bg-primary/10 text-primary dark:bg-primary/20', Icon: Sparkles },
    correcao: { label: 'Correção', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', Icon: Wrench },
};

const LIMITE_INICIAL = 8;
const INCREMENTO_CARREGAR_MAIS = 8;

const iniciais = (nome?: string | null): string => {
    if (!nome) return '?';
    const partes = nome.trim().split(/\s+/);
    const primeira = partes[0]?.[0] ?? '';
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
    return (primeira + ultima).toUpperCase() || '?';
};

// Diferente de Avisos do Sistema (que pode carregar um risco operacional
// sério), Novidades TI já chega aqui filtrado só pro que é novo pra essa
// pessoa e dos últimos 7 dias (ver AvisosSistemaGate.tsx) — lista curta por
// natureza, então não precisa forçar rolagem até o fim antes de liberar a
// confirmação.
const NovidadesTiModal = ({ itens, onVisualizado, onConfirmar }: NovidadesTiModalProps) => {
    const [aceite, setAceite] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [limite, setLimite] = useState(LIMITE_INICIAL);
    const jaMarcouVisto = useRef(false);
    const dialogRef = useRef<HTMLDivElement>(null);

    const itensVisiveis = itens.slice(0, limite);
    const temMais = itens.length > limite;
    const grupos = useMemo(() => agruparPorData(itensVisiveis), [itensVisiveis]);

    useEffect(() => {
        if (!jaMarcouVisto.current) {
            jaMarcouVisto.current = true;
            onVisualizado();
        }
    }, [onVisualizado]);

    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    const confirmar = async () => {
        setEnviando(true);
        try {
            await onConfirmar();
        } finally {
            setEnviando(false);
        }
    };

    // Bloqueante sempre — Novidades TI não tem onFechar/X/clique-fora, mesma
    // regra de "obrigatório confirmar antes de continuar" do resto de Avisos
    // do Sistema. Esc é sempre no-op aqui.
    const aoTeclar = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            return;
        }
        if (e.key !== 'Tab') return;
        const focaveis = dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focaveis || focaveis.length === 0) return;
        const primeiro = focaveis[0];
        const ultimo = focaveis[focaveis.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) {
            e.preventDefault();
            ultimo.focus();
        } else if (!e.shiftKey && document.activeElement === ultimo) {
            e.preventDefault();
            primeiro.focus();
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in">
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="novidades-ti-modal-titulo"
                tabIndex={-1}
                onKeyDown={aoTeclar}
                className="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col outline-none"
            >
                <div className="px-4 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary rounded-full w-fit mb-2">
                        <Sparkles size={14} />
                        <span className="text-xs font-bold">Novidades do Sistema</span>
                    </div>
                    <h2 id="novidades-ti-modal-titulo" className="text-xl font-bold text-gray-900 dark:text-white">Atualizações de TI</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Acompanhe o que a equipe entregou recentemente na Central de Solicitações.
                    </p>
                </div>

                <div className="px-4 py-4 overflow-y-auto flex-1 rounded space-y-6">
                    {grupos.map(([dataReferencia, itensDoDia]) => (
                        <div key={dataReferencia}>
                            <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">{dataReferenciaBr(dataReferencia)}</p>
                            <div className="space-y-3">
                                {itensDoDia.map((item) => {
                                    const cfg = CATEGORIA_CFG[item.categoria];
                                    return (
                                        <div key={item.id} className="bg-gray-50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-700 p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${cfg.color}`}>
                                                        <cfg.Icon size={11} />
                                                        {cfg.label}
                                                    </span>
                                                    <p className="font-semibold text-gray-900 dark:text-white mt-2">{item.titulo}</p>
                                                    {item.descricao && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{item.descricao}</p>
                                                    )}
                                                </div>
                                                <div
                                                    className="w-8 h-8 shrink-0 rounded-full bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center text-xs font-bold"
                                                    title={item.concluidoPorNome || undefined}
                                                >
                                                    {iniciais(item.concluidoPorNome)}
                                                </div>
                                            </div>
                                            {item.tag && (
                                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                                                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded text-[11px] font-semibold border border-gray-200 dark:border-gray-700">
                                                        {item.tag}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {temMais && (
                        <button
                            onClick={() => setLimite((l) => l + INCREMENTO_CARREGAR_MAIS)}
                            className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all text-sm font-semibold"
                        >
                            Carregar atualizações anteriores
                        </button>
                    )}
                </div>

                <div className="px-4 py-5 border-t border-gray-100 dark:border-gray-700 space-y-4">
                    <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={aceite}
                            onChange={(e) => setAceite(e.target.checked)}
                            className="mt-0.5 w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/30"
                        />
                        <span>Li e concordo com o comunicado acima.</span>
                    </label>

                    <button
                        onClick={confirmar}
                        disabled={!aceite || enviando}
                        className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-all"
                    >
                        {enviando ? 'Confirmando...' : 'Li e aceito'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NovidadesTiModal;
